import { baseApi as api } from "./baseApi";
export const addTagTypes = ["paste", "settings", "execution"] as const;
const injectedRtkApi = api
  .enhanceEndpoints({
    addTagTypes,
  })
  .injectEndpoints({
    endpoints: (build) => ({
      createPaste: build.mutation<CreatePasteApiResponse, CreatePasteApiArg>({
        query: (queryArg) => ({
          url: `/api/paste`,
          method: "POST",
          body: queryArg.createPasteRequest,
        }),
        invalidatesTags: ["paste"],
      }),
      getLanguages: build.query<GetLanguagesApiResponse, GetLanguagesApiArg>({
        query: () => ({ url: `/api/settings/languages` }),
        providesTags: ["settings"],
      }),
      getPaste: build.query<GetPasteApiResponse, GetPasteApiArg>({
        query: (queryArg) => ({ url: `/api/paste/${queryArg.pasteId}` }),
        providesTags: ["paste"],
      }),
      deletePaste: build.mutation<DeletePasteApiResponse, DeletePasteApiArg>({
        query: (queryArg) => ({
          url: `/api/paste/${queryArg.pasteId}`,
          method: "DELETE",
        }),
        invalidatesTags: ["paste"],
      }),
      startExecution: build.mutation<
        StartExecutionApiResponse,
        StartExecutionApiArg
      >({
        query: (queryArg) => ({
          url: `/api/paste/${queryArg.pasteId}/execution`,
          method: "POST",
        }),
        invalidatesTags: ["execution"],
      }),
      getExecution: build.query<GetExecutionApiResponse, GetExecutionApiArg>({
        query: (queryArg) => ({
          url: `/api/paste/${queryArg.pasteId}/execution`,
        }),
        providesTags: ["execution"],
      }),
    }),
    overrideExisting: false,
  });
export { injectedRtkApi as pastaApi };
export type CreatePasteApiResponse =
  /** status 201 Paste created (or existing identical paste referenced) */ PasteCreated;
export type CreatePasteApiArg = {
  createPasteRequest: CreatePasteRequest;
};
export type GetLanguagesApiResponse =
  /** status 200 Supported languages in display order, and the default */ LanguageSettings;
export type GetLanguagesApiArg = void;
export type GetPasteApiResponse = /** status 200 The paste */ Paste;
export type GetPasteApiArg = {
  pasteId: PasteId;
};
export type DeletePasteApiResponse = unknown;
export type DeletePasteApiArg = {
  pasteId: PasteId;
};
export type StartExecutionApiResponse =
  | /** status 200 The paste was already executed (or is being executed) */ Execution
  | /** status 202 Execution started */ Execution;
export type StartExecutionApiArg = {
  pasteId: PasteId;
};
export type GetExecutionApiResponse =
  /** status 200 The execution. Final executions never change; they are sent as
`Cache-Control: private, max-age=300` (shared caches must not keep them, since they
are removed with their paste). Unfinished ones are sent with `Cache-Control: no-store`.
 */ Execution;
export type GetExecutionApiArg = {
  pasteId: PasteId;
};
export type PasteId = string;
export type Language = string;
export type Title = string;
export type DeleteToken = string;
export type PasteCreated = {
  id: PasteId;
  language: Language;
  title?: Title;
  url: string;
  createdAt: string;
  deleteToken: DeleteToken;
};
export type Problem = {
  type: string;
  title: string;
  status: number;
  detail?: string;
  instance?: string;
};
export type CreatePasteRequest = {
  /** Paste text; must be well-formed Unicode. The maximum size in UTF-8 bytes is configured
    per deployment.
     */
  content: string;
  /** Syntax of the content; one of /api/settings/languages, which also names the default
    used when omitted. Part of the paste's identity: identical text in another language
    is a different paste.
     */
  language?: Language;
  /** Optional title. Surrounding whitespace is removed and a blank title means none; what
    remains must be a valid Title. Part of the paste's identity, like the language.
     */
  title?: string;
  /** Input fed to the program when the paste is executed; must be well-formed Unicode. The
    maximum size in UTF-8 bytes is configured per deployment. Part of the paste's identity.
     */
  stdin?: string;
  [key: string]: any;
};
export type LanguageOption = {
  id: Language;
  label: string;
  /** Whether pastes in this language can be executed on this deployment */
  executable: boolean;
};
export type LanguageSettings = {
  languages: LanguageOption[];
  default: Language;
};
export type Paste = {
  id: PasteId;
  content: string;
  language: Language;
  title?: Title;
  /** Input for executions; absent when there is none */
  stdin?: string;
  createdAt: string;
  sizeBytes: number;
};
export type ExecutionStatus =
  | "queued"
  | "building"
  | "running"
  | "completed"
  | "build_failed"
  | "failed"
  | "interrupted";
export type StreamOutput = {
  text: string;
  /** The program wrote more than the configured limit; the rest was dropped */
  truncated: boolean;
};
export type PhaseResult = {
  outcome: "exited" | "timed_out" | "out_of_memory";
  /** Present when the outcome is exited */
  exitCode?: number;
  stdout: StreamOutput;
  stderr: StreamOutput;
  durationMs: number;
};
export type Execution = {
  status: ExecutionStatus;
  requestedAt: string;
  /** Present once the status is final */
  finishedAt?: string;
  build?: PhaseResult;
  run?: PhaseResult;
};
export const {
  useCreatePasteMutation,
  useGetLanguagesQuery,
  useGetPasteQuery,
  useDeletePasteMutation,
  useStartExecutionMutation,
  useGetExecutionQuery,
} = injectedRtkApi;
